import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWindowSize } from 'react-use'
import { css } from '@emotion/core'
import { isEqual } from 'lodash'
import dayjs from 'dayjs'
import moment from 'moment'
import uuid from 'react-uuid'
import { wholeContainerStyle, mainColumnStyle, HEADER_HEIGHT } from '@/constants/layout'
import { AccountContext } from '@/contexts/account'
import { useRoomsSelectableHasRoomManagerPlugin } from '@/hooks/use-rooms-selectable'
import { Header } from '@/components/organisms/room-manager/header'
import { Button } from '@/components/atoms/button'
import { SideMenu } from '@/components/organisms/side-menu'
import { AssignPanel } from '@/components/pages/room-manager/_room-manager/assign-panel'
import { RoomRateDisplayModal } from '@/components/pages/room-manager/_room-manager/room-rate-display-modal'
import { makeDates } from '@/components/pages/room-manager/_daily/make-dates'
import { DailyListHead } from '@/components/pages/room-manager/_daily/daily-list-head'
import { DailyListBody } from '@/components/pages/room-manager/_daily/daily-list-body'
import { LeftGuestRoom } from '@/components/pages/room-manager/_daily/left-guest-room'
import { BulkOperationMenu } from '@/components/organisms/room-manager/bulk-operation/bulk-operation-menu'
import { LoadingFull } from '@/components/molecules/loading-full'
import { RoomSummaryBar } from '@/components/pages/room-manager/_room-manager/room-summary-bar'
import * as API from '@/apis/aipass'
import { setHasTlTwoWayPlugin, setHasRemoteLockPlugin } from '@/libs/plugins'
import { DateType } from '@/components/pages/room-manager/_daily/daily-props'
import { clickShowDetail } from '@/components/pages/room-manager/_daily/_body/click-show-detail'
import { GuestRoomAssignStatusResponseType, StatusType } from '@/models/room-manager/response/guest-room-assign-status'
import { ROOM_UNSPECIFIED } from '@/constants/room-manager'
import { ApprovedStatus } from '@/models/reservation-approved-status'
import { AdminReservationResponseType } from '@/models/reservation/admin-reservation'
import { GuestRoomSelectableResponseType } from '@/models/room-manager/response/guest-room-selectable'
import { SiteControllerStockType, TotalRemainingCountEachDateType } from '@/models/room-manager/response/site-controller-stock'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useHotelGuestRooms } from '@/hooks/use-hotel-guest-rooms'
import { RemainingRoomTotalBody } from '@/components/organisms/room-manager/remaining-room-total-body'
import { ReservationCreateWithRoomModal } from '@/components/organisms/accommodation-management/reservation-create-with-room-modal'
import { useHistory } from 'react-router-dom'
import { EditRoomType, useEditGuestRooms } from '@/hooks/use-edit-guest-rooms'
import { useCalendarScrollForAssign } from '@/hooks/use-calendar-scroll-for-assign'
import { listReservationRoomsToAssign } from '@/utils/reservation-rooms-to-assign'

const ROOM_NUMBER_PLACEHOLDER = '部屋番号を選択'

export const RoomManagerDaily: React.FC = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const windows = useWindowSize()
  const { errorHandler } = useErrorHandler()
  const { plugins, account } = useContext<any>(AccountContext)
  const params = require('query-string').parse(window.location.search)
  const reservationId: string | null = params.reservationId
  const guestRoomAssignId: string | null = params.guestRoomAssignId
  const clickedGuestRoomId: string | undefined = params.guestRoomId
  const roomNameCollapseStorageKey = `RoomNameCollapse-${account.hotel.id}`
  const roomOrderStorageKey = `RoomOrder-${account.hotel.id}`
  /**
   * Data for calendar tag display
   *
   * [Overview]
   * guestRooms:
   * Original data without date change
   * roomDatesChangedGuestRooms:
   * Update the date of each room in guestRooms when the check-in date and check-out date of each room are changed,
   * It is necessary to change the display of the calendar, but when the calendar is displayed, the original data of guestRooms is reacquired.
   * Since the data does not change, manage the date-changed guestRooms with roomDatesChangedGuestRooms
   */
  const [guestRooms, setGuestRooms] = useState<GuestRoomAssignStatusResponseType['guestRoomAssignStatusList']>()
  const [roomDatesChangedGuestRooms, setRoomDatesChangedGuestRooms] =
    useState<GuestRoomAssignStatusResponseType['guestRoomAssignStatusList']>()
  // Reservation information
  const [reservation, setReservation] = useState<AdminReservationResponseType['reservations']>()
  // Room list to edit
  const [editGuestRooms, setEditGuestRooms, handleFetchGuestRooms] = useEditGuestRooms()
  // List of selectable rooms
  const [roomsSelectable, handleFetchSelectableGuestRoom] = useRoomsSelectableHasRoomManagerPlugin()
  const [hotelGuestRooms, handleFetchGuestRoomForHotel] = useHotelGuestRooms()

  // Date management at the top of the calendar
  const [dates, setDates] = useState<DateType[]>([])
  // Date management at the top of the calendar
  const [roomManagerTopSelectDate, setRoomManagerTopSelectDate] = useState<moment.Moment>(moment())
  const today = dayjs().format('YYYY-MM-DD')
  const total = 10
  const start = -2
  const end = 7
  // Date management on the right side of the calendar
  const [beginDate, setBeginDate] = useState<string>(`${moment(roomManagerTopSelectDate).add(start, 'd').format('YYYY-MM-DD')}`)
  // Date management on the left side of the calendar
  const [endDate, setEndDate] = useState<string>(`${moment(roomManagerTopSelectDate).add(end, 'd').format('YYYY-MM-DD')}`)
  // IN/OUT check-in/check-out date
  const [reservationCheckinDate, setReservationCheckinDate] = useState<moment.Moment>(moment().hour(12).minute(0).second(0))
  const [reservationCheckoutDate, setReservationCheckoutDate] = useState<moment.Moment>(
    moment().add(1, 'days').hour(12).minute(0).second(0),
  )
  // For date management before changing the calendar (retaining reservationCheckinDate, reservationCheckoutDate before change)
  const [prevReservationCheckinDate, setPrevReservationCheckinDate] = useState<moment.Moment>(moment())
  const [prevReservationCheckoutDate, setPrevReservationCheckoutDate] = useState<moment.Moment>(moment().add(1, 'days'))
  // For creating a room in preparation
  const [preparation, setPreparation] = useState<boolean>(false)
  // for loading
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isLoadingReservationDetail, setIsLoadingReservationDetail] = useState<boolean>(false)
  // For handling when clicking the calendar room assignment
  const [isClickStatusBar, setIsClickStatusBar] = useState<boolean>(false)
  // For switch edit mode of assignPanel
  const [assignPanelEditMode, setAssignPanelEditMode] = useState<boolean>(false)
  // Flag for not scrolling when bar is clicked
  const [isBar, setIsBar] = useState<boolean>(false)

  // room name hidden list
  const [hiddenRoomNameList, setHiddenRoomNameList] = useState<string[]>(
    JSON.parse(localStorage.getItem(roomNameCollapseStorageKey) || '[]'),
  )
  const { rememberClickedRoom, scrollToReservationRooms, registerRoomRow, registerRoomGroup } = useCalendarScrollForAssign({
    guestRooms,
    hiddenRoomNameList,
  })
  // Site controller inventory management
  const [siteControllerStock, setSiteControllerStock] = useState<SiteControllerStockType | []>([])
  const [totalRemainingCountEachDate, setTotalRemainingCountEachDate] = useState<TotalRemainingCountEachDateType | {}>([])
  // Removed in-progress room
  const [deletedPreparationRooms, setDeletedPreparationRooms] = useState<EditRoomType[]>([])
  // For calendar room order, floor order change management
  const [order, setOrder] = useState<'room' | 'floor'>((localStorage.getItem(roomOrderStorageKey) as any) || 'room')
  // Currently selected room assignment Id
  const [roomAssignActiveId, setRoomAssignActiveId] = useState<string>('')
  const [preparationMemo, setPreparationMemo] = useState<string>('')

  // Tl2way plugin (reservation acquisition, inventory acquisition, inventory adjustment)
  const hasTlTwoWayPlugin = setHasTlTwoWayPlugin(plugins)
  // RemoteLock plugin (key management)
  const hasRemoteLockPlugin = setHasRemoteLockPlugin(plugins)
  const [showAssignPanel, setShowAssignPanel] = useState<boolean>(false)
  const [automaticAssignmentReservationsIds, setAutomaticAssignmentReservationsIds] = useState<string[] | undefined>()

  const [isReservationCreateModalOpen, setIsReservationCreateModalOpen] = useState<boolean>(false)
  const [isRoomRateModalOpen, setIsRoomRateModalOpen] = useState<boolean>(false)

  // editGuestRooms のうち、カレンダーに影響する割り当て情報（roomNumber/guestRoomId/日付/準備タイプ）の前回値を保持する。
  // selectableRooms のみの更新（fetchSelectableGuestRoom 後）では API を呼ばないようにするため。
  const prevAssignmentSignatureRef = useRef<string>('')

  /**
   * Get calendar assignment data
   */
  const fetchGuestRoomAssignStatus = async (
    selectOrder?: 'room' | 'floor',
    guestRoom?: GuestRoomAssignStatusResponseType['guestRoomAssignStatusList'],
  ) => {
    const paramsOrder = selectOrder ? selectOrder : order

    if (editGuestRooms.length !== 0 && editGuestRooms[0].guestRoomId) {
      if (guestRoom) {
        mergeEditGuestRooms(guestRoom)
        return
      } else if (roomDatesChangedGuestRooms) {
        mergeEditGuestRooms(roomDatesChangedGuestRooms)
        return
      }
    }

    setIsLoading(true)
    await API.fetchGuestRoomAssignStatus(paramsOrder, beginDate, endDate)
      .then((res: GuestRoomAssignStatusResponseType) => {
        // Display tag if room number is included in assignPanel
        if (!res?.guestRoomAssignStatusList) {
          return
        }
        if (editGuestRooms.length !== 0 && editGuestRooms[0].guestRoomId) {
          mergeEditGuestRooms(res.guestRoomAssignStatusList)
        } else {
          setGuestRooms(res.guestRoomAssignStatusList)
        }
      })
      .finally(() => {
        finishLoading()
      })
  }

  /**
   * Reservation information acquisition
   */
  const fetchReservationDetail = async (reservationId: string) => {
    try {
      setIsLoading(true)
      setIsLoadingReservationDetail(true)

      const res = await API.fetchReservationDetail({ reservationId })

      if (!res) return console.warn('fetchReservation return empty response')

      const reservation: AdminReservationResponseType['reservations'] = res.reservations

      setReservation(reservation)
      setReservationCheckinDate(moment(reservation.checkinDate))
      setPrevReservationCheckinDate(moment(reservation.checkinDate))
      setPrevReservationCheckoutDate(moment(reservation.checkoutDate))
      // If there is no check-out date, set the day after the check-in date
      const checkoutDate = reservation.checkoutDate ? moment(reservation.checkoutDate) : moment(reservation.checkinDate).add(1, 'days')
      setReservationCheckoutDate(checkoutDate)
      const editRooms = await handleFetchGuestRooms({ reservationId })
      // 部屋の行が無い予約では、右パネルが足す部屋と同じ部屋タイプの見出しへ動かす
      const [reservationRoomToAssign] = listReservationRoomsToAssign(reservation.reservationRoom)
      scrollToReservationRooms(editRooms, { reservationId, reservationRoomTypeName: reservationRoomToAssign?.typeNameJa })

      if (editRooms.length) {
        await handleManyGuestRoomsFetch(editRooms)
      } else {
        // Set initial value for adding rooms if editGuestRooms is empty (no rooms assigned)
        const initialEditRoom = {
          roomTypeId: null,
          typeNameJa: null,
          typeNameEn: null,
          guestRoomId: null,
          roomNumber: null,
          assignCheckinDate: moment(reservation.checkinDate).format('YYYY-MM-DD HH:mm:ss'),
          assignCheckoutDate: moment(checkoutDate).format('YYYY-MM-DD HH:mm:ss'),
          editingId: `${uuid()}`,
        }
        setEditGuestRooms([initialEditRoom])
        // Acquired during the stay period of the reservation
        await fetchSelectableGuestRoom(moment(reservation.checkinDate), moment(reservation.checkoutDate), [initialEditRoom])
      }
      setRoomManagerTopSelectDate(moment(reservation.checkinDate))
    } finally {
      setIsLoadingReservationDetail(false)
      setIsLoading(false)
    }
  }

  const finishLoading = () => {
    if (!isLoadingReservationDetail) setIsLoading(false)
  }

  const handleManyGuestRoomsFetch = async editRooms => {
    let lastFetchSelectableGuestRoomParams: any = undefined
    let lastEditGuestRooms = editRooms

    for (const [index, room] of (editRooms || []).entries()) {
      const beginDate = moment(room.assignCheckinDate)
      const endDate = moment(room.assignCheckoutDate)

      const _beginDate = beginDate.format('YYYY-MM-DD')
      // Set the next day after the check-in date if the check-out date is not set
      const _endDate = endDate.isValid() ? endDate?.format('YYYY-MM-DD') : beginDate.add(1, 'days').format('YYYY-MM-DD')

      // Only the selected room assignment can be assigned again in the same period

      const currentFetchSelectableGuestRoomParams = [_beginDate, _endDate, reservationId ? reservationId : undefined]

      if (isEqual(lastFetchSelectableGuestRoomParams, currentFetchSelectableGuestRoomParams)) continue

      const guestRooms = await handleFetchSelectableGuestRoom(_beginDate, _endDate, reservationId ? reservationId : undefined)
      const currentEditGuestRooms = updateEditGuestRoomsSelectableRooms(guestRooms, editRooms, index, true)

      if (!isEqual(lastEditGuestRooms, currentEditGuestRooms)) setEditGuestRooms(lastEditGuestRooms)

      lastFetchSelectableGuestRoomParams = currentFetchSelectableGuestRoomParams
      lastEditGuestRooms = currentEditGuestRooms
    }
  }

  /**
   * Get Available Rooms
   */
  const fetchSelectableGuestRoom = async (
    beginDate: moment.Moment,
    endDate: moment.Moment,
    editRooms?: EditRoomType[],
    roomIndex?: number,
  ) => {
    try {
      const _beginDate = beginDate.format('YYYY-MM-DD')
      // Set the next day after the check-in date if the check-out date is not set
      const _endDate = endDate.isValid() ? endDate?.format('YYYY-MM-DD') : beginDate.clone().add(1, 'days').format('YYYY-MM-DD')
      setIsLoading(true)

      const selectableRooms = await handleFetchSelectableGuestRoom(_beginDate, _endDate, reservationId ? reservationId : undefined)
      updateEditGuestRoomsSelectableRooms(selectableRooms, editRooms, roomIndex)
    } finally {
      finishLoading()
    }
  }

  /**
   * 予約全体の IN/OUT 変更時に呼ぶ専用ハンドラ。
   * 空室取得後に各部屋の維持/解除を判定し、空室不足の場合はアラートを表示する。
   * DatePicker (checkin/checkout) からのみ呼ばれる。
   */
  const fetchSelectableGuestRoomForPeriodChange = async (beginDate: moment.Moment, endDate: moment.Moment) => {
    try {
      const _beginDate = beginDate.format('YYYY-MM-DD')
      const _endDate = endDate.isValid() ? endDate?.format('YYYY-MM-DD') : beginDate.clone().add(1, 'days').format('YYYY-MM-DD')
      setIsLoading(true)

      const selectableRooms = await handleFetchSelectableGuestRoom(_beginDate, _endDate, reservationId ? reservationId : undefined)
      const availableIds = new Set(selectableRooms.flatMap(type => type.rooms.map(r => r.guestRoomId)))

      const unavailableRoomNumbers = editGuestRooms
        .map(room => getUnavailableRoomNumber(room, availableIds))
        .filter((n): n is string => n !== null)

      // assignCheckinDate の競合を防ぐため prev を使う
      setEditGuestRooms(prev => prev.map(room => resolveRoomAfterPeriodChange(room, selectableRooms, availableIds)))

      if (unavailableRoomNumbers.length > 0) {
        window.alert(
          t('Room is not available for the selected period. Please assign another room number.', {
            rooms: unavailableRoomNumbers.join(', '),
          }),
        )
      }
    } finally {
      finishLoading()
    }
  }

  const resolveRoomAfterPeriodChange = (
    room: EditRoomType,
    newSelectableRooms: GuestRoomSelectableResponseType['guestRooms'],
    availableIds: Set<string>,
  ): EditRoomType => {
    const isAvailable = !!room.guestRoomId && availableIds.has(room.guestRoomId)

    if (isAvailable) {
      return { ...room, selectableRooms: newSelectableRooms }
    }

    const targetType = newSelectableRooms.find(t => t.typeNameJa === room.typeNameJa)
    const notSpecified = targetType?.rooms.find(r => r.roomNumber === ROOM_UNSPECIFIED)

    return {
      ...room,
      selectableRooms: newSelectableRooms,
      roomNumber: notSpecified?.roomNumber ?? ROOM_NUMBER_PLACEHOLDER,
      guestRoomId: notSpecified?.guestRoomId ?? '',
    }
  }

  /** 期間変更で使用不可になった部屋番号を返す（アラート表示用）。使用可能または未指定の場合は null */
  const getUnavailableRoomNumber = (room: EditRoomType, availableIds: Set<string>): string | null => {
    const isAvailable = !!room.guestRoomId && availableIds.has(room.guestRoomId)
    const isSpecified = room.roomNumber !== ROOM_UNSPECIFIED && room.roomNumber !== ROOM_NUMBER_PLACEHOLDER
    return !isAvailable && isSpecified ? (room.roomNumber ?? null) : null
  }

  /**
   * Recreate Assignments panel data
   */
  const reCreateAssignPanelDetail = async (reservationId: string) => {
    await fetchReservationDetail(reservationId)
    setRoomDatesChangedGuestRooms(undefined)
    setAssignPanelEditMode(false)
  }

  /**
   * Site Controller Inventory Acquisition
   */
  const fetchSiteControllerStock = async (beginDate: moment.Moment, endDate: moment.Moment) => {
    // const yesterday = moment().add(-1, 'day')
    // beginDateが昨日より前なら昨日を入れる
    const _beginDate = moment(beginDate)
    const _endDate = moment(endDate)

    if (order === 'room') {
      await fetchStock(moment(_beginDate).format('YYYY-MM-DD'), moment(_endDate).format('YYYY-MM-DD'))
    }
  }

  const fetchStock = async (beginDate: string, endDate: string) => {
    try {
      const res = await API.fetchAdminReservationStock(beginDate, endDate)

      //   const newStockResultList = formatStockResultList(res.stockResultList)
      setSiteControllerStock(res.stocks)
      setTotalRemainingCountEachDate(res.totalRemainingCountEachDate)
    } catch (error) {
      errorHandler(error)
    }
  }

  /**
   * 一括操作の完了後、カレンダーとサイトコントローラー在庫を取り直す
   */
  const reloadAfterBulkOperation = async () => {
    setIsLoading(true)
    await fetchSiteControllerStock(moment(beginDate), moment(endDate))
    // 最後に呼ぶ: 内部で finishLoading() までを担う
    await initializeGuestRoomsStatuses(true)
  }

  /**
   * What to do when clicking the ready checkbox
   */
  const changePreparation = (preparation: boolean) => {
    /**
     * When the check box under preparation is activated Extract the difference of each room and set it to the delete room under preparation array
     * When the check box under preparation is deactivated, the one with the current editGuestRooms assignment to the preparing room deletion array
     *
     * The check box in preparation is only available when creating a new assignment or editing in preparation
     * The guestRoomAssignId in editGuestRooms will be the guestRoomAssignId in preparation
     */
    if (preparation) {
      const _editGuestRooms: typeof editGuestRooms = []
      const combinedRooms = [
        ...editGuestRooms.filter(room => room.guestRoomAssignId),
        ...deletedPreparationRooms.filter(room => room.guestRoomAssignId),
      ]
      combinedRooms.filter(function (val, i, self) {
        return i === self.indexOf(val)
      })

      // set for delete
      combinedRooms.forEach(room => {
        _editGuestRooms.push({
          guestRoomId: null,
          roomTypeId: null,
          typeNameEn: null,
          roomNumber: null,
          typeNameJa: null,
          assignCheckinDate: null,
          assignCheckoutDate: null,
          guestRoomAssignId: room.guestRoomAssignId,
          editingId: room.editingId,
        })
        // room.guestRoomAssignId here is the guestRoomAssignId in preparation, so delete it.
        room.guestRoomAssignId = undefined
      })

      setDeletedPreparationRooms(_editGuestRooms)
    } else {
      if (editGuestRooms.length) {
        const _editGuestRooms: typeof editGuestRooms = []
        editGuestRooms.forEach(room => {
          if (room.guestRoomAssignId) {
            _editGuestRooms.push({
              guestRoomId: null,
              roomTypeId: null,
              typeNameEn: null,
              roomNumber: null,
              typeNameJa: null,
              assignCheckinDate: null,
              assignCheckoutDate: null,
              guestRoomAssignId: room.guestRoomAssignId,
              editingId: room.editingId,
            })
            // room.guestRoomAssignId here is the guestRoomAssignId in preparation, so delete it.
            room.guestRoomAssignId = undefined
          }
        })
        setDeletedPreparationRooms(_editGuestRooms)
      }
    }

    /**
     * Update the assignStatus of the displayed tag to be assigned and change the color
     */
    let _guestRoom: GuestRoomAssignStatusResponseType['guestRoomAssignStatusList']
    if (roomDatesChangedGuestRooms) {
      _guestRoom = { ...roomDatesChangedGuestRooms }
    } else {
      _guestRoom = { ...guestRooms }
    }
    Object.keys(_guestRoom).forEach(floor => {
      Object.keys(_guestRoom[floor]).forEach(guestRoomNumber => {
        _guestRoom[floor][guestRoomNumber].statuses.forEach(status => {
          if (status.assignStatus) {
            status.assignStatus = preparation ? 'preparation' : ApprovedStatus.Reservation
          }
        })
      })
    })
    setPreparation(preparation)
    setAssignPanelEditMode(true)
    setGuestRooms(_guestRoom)
    if (roomDatesChangedGuestRooms) {
      setRoomDatesChangedGuestRooms(_guestRoom)
    }
  }

  /**
   * What happens when you click on the ready bar
   *
   */
  const onClickPreparationRoom = async (guestRoomAssignId: string | null) => {
    if (!guestRooms) {
      return
    }

    if (!guestRoomAssignId) {
      setPreparation(false)
      setPreparationMemo('')
      return
    }

    // Delete current reservation information
    setReservation(undefined)
    const preparationRoom: EditRoomType = {
      roomTypeId: null,
      typeNameJa: null,
      typeNameEn: null,
      roomNumber: null,
      guestRoomId: null,
    }

    Object.keys(guestRooms).forEach(floor => {
      // Retrieve guestRooms room number using room type (or floor number)
      Object.keys(guestRooms[floor]).forEach(guestRoomNumber => {
        // Determining the room number tag currently assigned (to return to the initial state of data acquisition)
        guestRooms[floor][guestRoomNumber].statuses.some(status => {
          if (status.guestRoomAssignId === guestRoomAssignId) {
            preparationRoom.typeNameJa = guestRooms[floor][guestRoomNumber].originRoomType
            preparationRoom.typeNameEn = guestRooms[floor][guestRoomNumber].originRoomType
            preparationRoom.roomNumber = guestRoomNumber
            preparationRoom.guestRoomAssignId = status.guestRoomAssignId
            preparationRoom.assignCheckinDate = status.assignCheckinDate
            preparationRoom.assignCheckoutDate = status.assignCheckoutDate
            preparationRoom.preparationMemo = status.preparationMemo
            preparationRoom.preparationType = status.preparationType
            if (status.guestRoomId) {
              preparationRoom.guestRoomId = status.guestRoomId
            }
            return true
          }
        })
      })
    })
    // Bar activation
    setPreparation(true)
    setRoomAssignActiveId(guestRoomAssignId)
    setReservationCheckinDate(moment(preparationRoom.assignCheckinDate))
    setReservationCheckoutDate(moment(preparationRoom.assignCheckoutDate))
    setPreparationMemo(preparationRoom.preparationMemo ?? '')
    // Add target rooms in preparation to editGuestRooms (remove other rooms)
    setEditGuestRooms([preparationRoom])
    setDeletedPreparationRooms([])
    fetchSelectableGuestRoom(moment(preparationRoom.assignCheckinDate), moment(preparationRoom.assignCheckoutDate), [preparationRoom])
  }

  /**
   * scroll back to the left edge
   */
  const calendarScrollReset = () => {
    const container = document.getElementById('roomsContainer')
    container!.scrollTo({
      left: 0,
    })
  }

  /**
   * Initialize (delete) the place where the current tag of guestRooms is displayed
   */
  const initializeGuestRoomsStatuses = async (fllInitialize?: boolean) => {
    // Refetch data and fully initialize guestRooms statuses
    if (fllInitialize) {
      await API.fetchGuestRoomAssignStatus(order, beginDate, endDate)
        .then((res: GuestRoomAssignStatusResponseType) => {
          if (res?.guestRoomAssignStatusList) setGuestRooms(res.guestRoomAssignStatusList)
        })
        .finally(() => {
          finishLoading()
        })
      setRoomDatesChangedGuestRooms(undefined)
      return
    }

    let _guestRooms: GuestRoomAssignStatusResponseType['guestRoomAssignStatusList']
    if (roomDatesChangedGuestRooms) {
      _guestRooms = { ...roomDatesChangedGuestRooms }
    } else {
      _guestRooms = { ...guestRooms }
    }
    Object.keys(_guestRooms).forEach(floor => {
      // Retrieve guestRooms room number using room type (or floor number)
      Object.keys(_guestRooms[floor]).forEach(guestRoomNumber => {
        // Determining the room number tag currently assigned (to return to the initial state of data acquisition)
        const assignInitialStatuses = _guestRooms[floor][guestRoomNumber].statuses.filter(
          status =>
            Object.prototype.hasOwnProperty.call(status, 'guestRoomAssignId') ||
            Object.prototype.hasOwnProperty.call(status, 'guestRoomId'),
        )
        if (assignInitialStatuses.length !== 0) {
          _guestRooms[floor][guestRoomNumber].statuses = assignInitialStatuses
        } else {
          // Initialize statuses if not the currently assigned room number tag
          _guestRooms[floor][guestRoomNumber].statuses = []
        }
      })
    })
    setGuestRooms(_guestRooms)
    setPrevReservationCheckinDate(reservationCheckinDate)
    setPrevReservationCheckoutDate(reservationCheckoutDate)
    setReservationCheckinDate(reservationCheckinDate)
    setReservationCheckoutDate(reservationCheckoutDate)
  }

  /**
   * eset/update selectableRooms in ditGuestRooms
   */
  const updateEditGuestRoomsSelectableRooms = (
    selectableRooms: GuestRoomSelectableResponseType['guestRooms'],
    editRooms?: EditRoomType[],
    roomIndex?: number,
    returnValue?: boolean,
  ) => {
    /**
     * If there is roomIndex, specify the target Index of editGuestRooms and update guestRooms
     * If there is no roomIndex, it is the first time or the accommodation date of the reservation is changed, so editRooms Initialize all guestRooms
     */
    let _editGuestRooms: EditRoomType[]
    if (editRooms) {
      _editGuestRooms = [...editRooms]
    } else {
      _editGuestRooms = [...editGuestRooms]
    }

    // Filter by assignCheckinDate to avoid adding a room when a reserved room is deleted
    _editGuestRooms = _editGuestRooms.filter(room => !!room.assignCheckinDate)
    if (roomIndex) {
      if (_editGuestRooms?.[roomIndex]) _editGuestRooms[roomIndex].selectableRooms = selectableRooms
    } else {
      _editGuestRooms.forEach(room => {
        room.selectableRooms = selectableRooms
      })
    }

    if (returnValue) return _editGuestRooms

    setEditGuestRooms(_editGuestRooms)
  }

  const filterOldEditingRooms = (showRooms: GuestRoomAssignStatusResponseType['guestRoomAssignStatusList']) => {
    const editingIds = editGuestRooms.map(editRoom => editRoom.editingId).filter(editingId => !!editingId)
    Object.keys(showRooms).forEach(floor => {
      Object.keys(showRooms[floor]).forEach(roomNumber => {
        showRooms[floor][roomNumber].statuses = showRooms[floor][roomNumber].statuses.filter(
          room => !(room.editingId && editingIds.includes(room.editingId)),
        )
      })
    })
  }

  /**
   * Edit guestRoom to show selected tags
   * If roomDatesChangedGuestRooms exists (if room dates have changed) guestRoomData is passed roomDatesChangedGuestRooms
   */
  const mergeEditGuestRooms = (guestRoomData: GuestRoomAssignStatusResponseType['guestRoomAssignStatusList']) => {
    /**
     * TODO: Fixed here so that tags under preparation can also be displayed
     * Reservation, check-in, and check-out seem to work as they are
     */
    const newGuestRooms = { ...guestRoomData }

    filterOldEditingRooms(newGuestRooms)

    // Get the room type (or floor) from guestRooms
    Object.keys(newGuestRooms).forEach(floor => {
      /**
       * If the check-in/check-out of each room is changed, the data management of guestRooms is changed to roomDatesChangedGuestRooms
       * Therefore, even if the data is reacquired, the tag will not be updated, so it is necessary to delete the tag of the room number that is not in editGuestRooms.
       */
      if (roomDatesChangedGuestRooms) {
        const roomsNumbers = editGuestRooms.map(room => room.roomNumber)
        // Extract room numbers from newGuestRooms that do not match the room numbers in the currently selected editGuestRooms
        const changeRoomNumberRooms: string[] = Object.keys(newGuestRooms[floor]).filter((guestRoomNumber: string) => {
          return !roomsNumbers.includes(guestRoomNumber)
        })
        if (changeRoomNumberRooms.length !== 0) {
          // Remove tags with mismatched room numbers, but leave tags with currently assigned room numbers
          changeRoomNumberRooms.forEach(changeRoomNumber => {
            // Determining the room number tag currently assigned (to return to the initial state of data acquisition)
            const assignInitialStatuses = newGuestRooms[floor][changeRoomNumber].statuses.filter(
              status =>
                Object.prototype.hasOwnProperty.call(status, 'guestRoomAssignId') ||
                Object.prototype.hasOwnProperty.call(status, 'guestRoomId'),
            )
            if (assignInitialStatuses.length !== 0) {
              roomDatesChangedGuestRooms[floor][changeRoomNumber].statuses = assignInitialStatuses
            } else {
              // Initialize statuses if not the currently assigned room number tag
              newGuestRooms[floor][changeRoomNumber].statuses = []
            }
          })
        }
      }

      Object.keys(newGuestRooms[floor]).forEach(guestRoomNumber => {
        if (guestRoomNumber === ROOM_UNSPECIFIED) return
        // Is the room number selected in assignPanel?
        const selectedRoomNumberRooms: any[] = editGuestRooms.filter(
          editGuestRoom => editGuestRoom.guestRoomId === newGuestRooms[floor][guestRoomNumber].guestRoomId,
        )
        /**
         * Display tags by inserting assignStatus at the beginning of the array (inserted at the beginning so that they are at the bottom when overlapping)
         */
        if (selectedRoomNumberRooms.length) {
          // Room assignment from customer management
          if (reservation && String(reservation.approvedStatus)) {
            selectedRoomNumberRooms.forEach(selectedRoomNumberRoom => {
              const editingId = selectedRoomNumberRoom.editingId || selectedRoomNumberRoom.guestRoomAssignId
              // Create an array for judgment so as not to add if there is already assignStatus
              const existsAlreadyStatuses = newGuestRooms[floor][guestRoomNumber].statuses.filter(status => {
                return status.editingId === editingId || status.assignStatus === 'preparation'
              })
              if (!existsAlreadyStatuses.length) {
                newGuestRooms[floor][guestRoomNumber].statuses.unshift({
                  assignStatus: preparation
                    ? 'preparation'
                    : String(selectedRoomNumberRoom.assignStatus) || String(reservation.approvedStatus),
                  assignCheckinDate: moment(selectedRoomNumberRoom.assignCheckinDate).format('YYYY-MM-DD'),
                  changeAssignCheckinDate: moment(selectedRoomNumberRoom.assignCheckinDate).format('YYYY-MM-DD'),
                  assignCheckoutDate: moment(selectedRoomNumberRoom.assignCheckoutDate).format('YYYY-MM-DD'),
                  changeAssignCheckoutDate: moment(selectedRoomNumberRoom.assignCheckoutDate).format('YYYY-MM-DD'),
                  editingId,
                })
              }
            })
          } else {
            // New reservation room assignment or preparation assignment
            selectedRoomNumberRooms.forEach(selectedRoomNumberRoom => {
              const editingId = selectedRoomNumberRoom.editingId || selectedRoomNumberRoom.guestRoomAssignId
              // Create an array for judgment so as not to add if there is already assignStatus
              const existsAlreadyStatuses = newGuestRooms[floor][guestRoomNumber].statuses.filter(status => {
                return status.editingId === editingId || status.assignStatus === 'preparation'
              })
              if (!existsAlreadyStatuses.length) {
                newGuestRooms[floor][guestRoomNumber].statuses.unshift({
                  assignStatus: preparation ? 'preparation' : ApprovedStatus.Reservation,
                  assignCheckinDate: moment(selectedRoomNumberRoom.assignCheckinDate).format('YYYY-MM-DD'),
                  changeAssignCheckinDate: moment(selectedRoomNumberRoom.assignCheckinDate).format('YYYY-MM-DD'),
                  assignCheckoutDate: moment(selectedRoomNumberRoom.assignCheckoutDate).format('YYYY-MM-DD'),
                  changeAssignCheckoutDate: moment(selectedRoomNumberRoom.assignCheckoutDate).format('YYYY-MM-DD'),
                  editingId: selectedRoomNumberRoom.editingId || selectedRoomNumberRoom.guestRoomAssignId,
                })
              }
            })
          }
        }

        // @memo たぶん使っていない。フロア順だとフロア名が入ってしまう
        newGuestRooms[floor][guestRoomNumber].statuses.forEach(status => {
          if (status.guestRoomAssignId) {
            status.roomType = newGuestRooms[floor][guestRoomNumber].roomType
          }
        })
      })
    })

    if (!isEqual(newGuestRooms, guestRooms)) setGuestRooms(newGuestRooms)
    if (roomDatesChangedGuestRooms) {
      setRoomDatesChangedGuestRooms(newGuestRooms)
    }
  }

  /**
   * Assignment cancellation processing
   */
  const clearRoomAssign = useCallback(() => {
    window.history.pushState('', '', '/room-manager/daily')
    // Initialize State for each room assignment
    setRoomAssignActiveId('')
    setEditGuestRooms([])
    setReservation(undefined)
    setShowAssignPanel(false)
  }, [setRoomAssignActiveId, setEditGuestRooms, setReservation, setShowAssignPanel])

  const onClickStatusBar = useCallback(
    (statusData: StatusType, guestRoomId?: string) => {
      // Deselect the currently selected bar if you click again
      const params = require('query-string').parse(window.location.search)
      if (statusData.reservationId === params.reservationId || statusData.guestRoomAssignId === params.guestRoomAssignId) {
        return clearRoomAssign()
      }

      if (statusData.reservationId) rememberClickedRoom(statusData.reservationId, guestRoomId)
      clickShowDetail(statusData)
      setIsClickStatusBar(true)
      setIsBar(true)
    },
    [clearRoomAssign, rememberClickedRoom, setIsClickStatusBar, setIsBar],
  )

  const clickToFreeDate = useCallback(
    ({ date, group, roomNumber, guestRoomId }: { date: any; group: string; roomNumber: string; guestRoomId: string }) => {
      if (!date.days || !guestRooms || !guestRooms[group][roomNumber]) return

      if (!guestRoomId) return alert('Unknown guest room')

      const checkinDate = moment(date.days).hour(12).minute(0).second(0)
      const checkoutDate = moment(date.days).add(1, 'days').hour(12).minute(0).second(0)
      const _editGuestRooms = [...editGuestRooms]

      let selectRoomType: string
      if (order === 'floor') {
        selectRoomType =
          hotelGuestRooms.find(room =>
            (room.roomNumber ? room.roomNumber[group] || [] : []).map(info => info.roomNumber).includes(roomNumber),
          )?.typeNameJa || ''
      } else {
        selectRoomType = guestRooms[group][roomNumber].roomType
      }

      _editGuestRooms.push({
        assignStatus: reservation?.approvedStatus ? `${reservation.approvedStatus}` : ApprovedStatus.Reservation,
        roomTypeId: undefined,
        typeNameJa: selectRoomType,
        typeNameEn: selectRoomType,
        guestRoomId,
        roomNumber,
        assignCheckinDate: moment(checkinDate).format('YYYY-MM-DD HH:mm:ss'),
        assignCheckoutDate: moment(checkoutDate).format('YYYY-MM-DD HH:mm:ss'),
        editingId: `${uuid()}`,
      })

      const addedIndex = _editGuestRooms.length - 1
      fetchSelectableGuestRoom(checkinDate, checkoutDate, _editGuestRooms, addedIndex)

      let minCheckinDate, maxCheckoutDate

      for (const editGuestRoom of _editGuestRooms) {
        if (!minCheckinDate || moment(minCheckinDate).isAfter(editGuestRoom.assignCheckinDate)) {
          minCheckinDate = moment(editGuestRoom.assignCheckinDate)
        }
        if (!maxCheckoutDate || moment(maxCheckoutDate).isBefore(editGuestRoom.assignCheckoutDate)) {
          maxCheckoutDate = moment(editGuestRoom.assignCheckoutDate)
        }
      }

      setReservationCheckinDate(minCheckinDate)
      setReservationCheckoutDate(maxCheckoutDate)

      setEditGuestRooms(_editGuestRooms)
      setAssignPanelEditMode(true)
      setShowAssignPanel(true)
      setIsClickStatusBar(true)
    },
    [
      guestRooms,
      editGuestRooms,
      order,
      hotelGuestRooms,
      reservation,
      fetchSelectableGuestRoom,
      setReservationCheckinDate,
      setReservationCheckoutDate,
      setEditGuestRooms,
      setAssignPanelEditMode,
      setShowAssignPanel,
      setIsClickStatusBar,
    ],
  )

  const makeAutomaticAssignment = async () => {
    try {
      setIsLoading(true)

      const res = await API.guestRoomAutoAssign({ reservationIds: automaticAssignmentReservationsIds, pageType: 'room_management' })

      if (res?.errors) errorHandler({ response: { data: res } })

      setAutomaticAssignmentReservationsIds(undefined)
      fetchGuestRoomAssignStatus(order)
      if (reservation) fetchReservationDetail(reservation.reservationId)
    } catch (error) {
      errorHandler(error)
    } finally {
      finishLoading()
    }
  }

  /**
   * Reservation information acquisition handling
   */
  useEffect(() => {
    if (!reservationId) {
      return
    }
    // 表のバーのクリックで覚えた部屋を消さないよう、URL に部屋があるときだけ覚えさせる
    if (clickedGuestRoomId) rememberClickedRoom(reservationId, clickedGuestRoomId)
    setRoomAssignActiveId(reservationId)
    reCreateAssignPanelDetail(reservationId)
    setPreparation(false)
    setIsClickStatusBar(false)
  }, [reservationId, clickedGuestRoomId])

  /**
   * Preparing bar handling
   */
  useEffect(() => {
    onClickPreparationRoom(guestRoomAssignId)
  }, [guestRoomAssignId])

  /**
   * upper calendar handling
   */
  useEffect(() => {
    const _beginDate = moment(roomManagerTopSelectDate).add(start, 'd').format('YYYY-MM-DD')
    const _endDate = moment(roomManagerTopSelectDate).add(end, 'd').format('YYYY-MM-DD')
    setDates(makeDates(roomManagerTopSelectDate, total, start))
    setBeginDate(_beginDate)
    setEndDate(_endDate)

    if (!isBar) {
      calendarScrollReset()
    }

    // Redraw the calendar if you change the date at the top of the calendar
    // eslint-disable-next-line no-extra-semi
    ;(async () => {
      setIsLoading(true)
      await API.fetchGuestRoomAssignStatus(order, _beginDate, _endDate)
        .then((res: GuestRoomAssignStatusResponseType) => {
          if (!res?.guestRoomAssignStatusList) {
            return
          }
          if (editGuestRooms.length !== 0 && editGuestRooms[0].guestRoomId) {
            mergeEditGuestRooms(res.guestRoomAssignStatusList)
          } else {
            setGuestRooms(res.guestRoomAssignStatusList)
          }
        })
        .finally(() => {
          finishLoading()
        })
      if (plugins) {
        await fetchSiteControllerStock(moment(_beginDate), moment(_endDate))
      }
    })()
  }, [roomManagerTopSelectDate])

  // For handling IN and OUT of the assign panel
  useEffect(() => {
    // 部屋番号の維持/解除は fetchSelectableGuestRoom 完了後に空室候補と照合して判定するため、ここでは各部屋のアサイン日付のみ更新する
    if (!isClickStatusBar) {
      const updated = editGuestRooms.map(room => {
        const newRoom = { ...room }

        if (
          moment(room.assignCheckinDate).format('YYYY-MM-DD') === moment(prevReservationCheckinDate).format('YYYY-MM-DD') ||
          !moment(room.assignCheckinDate).isBetween(reservationCheckinDate, reservationCheckoutDate) ||
          !room.assignCheckinDate
        ) {
          newRoom.assignCheckinDate = moment(reservationCheckinDate).format('YYYY-MM-DD 12:00:00')
        }

        if (
          moment(room.assignCheckoutDate).format('YYYY-MM-DD') === moment(prevReservationCheckoutDate).format('YYYY-MM-DD') ||
          !moment(room.assignCheckoutDate).isBetween(reservationCheckinDate, reservationCheckoutDate) ||
          !room.assignCheckoutDate
        ) {
          newRoom.assignCheckoutDate = moment(reservationCheckoutDate).format('YYYY-MM-DD 12:00:00')
        }

        return newRoom
      })
      setEditGuestRooms(updated)
    }

    setIsClickStatusBar(false)
  }, [reservationCheckinDate, reservationCheckoutDate])

  useEffect(() => {
    // カレンダーに影響する割り当てフィールドのみでシグネチャを生成し、
    // selectableRooms だけが変わったとき（fetchSelectableGuestRoom 完了後）には API を呼ばない
    const signature = editGuestRooms
      .map(r => `${r.editingId}|${r.guestRoomId}|${r.roomNumber}|${r.assignCheckinDate}|${r.assignCheckoutDate}|${r.preparationType}`)
      .join('/')

    const assignmentChanged = signature !== prevAssignmentSignatureRef.current
    prevAssignmentSignatureRef.current = signature

    const hasAssignedRoom = editGuestRooms.some(room => room.guestRoomId || typeof room.preparationType === 'number')

    if (assignmentChanged && (hasAssignedRoom || editGuestRooms.length === 0)) {
      fetchGuestRoomAssignStatus(order)
    }

    if (!reservation && !guestRoomAssignId && !editGuestRooms.length) {
      setShowAssignPanel(false)
    }
  }, [editGuestRooms])

  useEffect(() => {
    if (reservation || guestRoomAssignId) setShowAssignPanel(true)
    else setShowAssignPanel(false)
  }, [reservation, guestRoomAssignId])

  useEffect(() => {
    handleFetchGuestRoomForHotel()
  }, [])

  return (
    <div css={wholeContainerStyle}>
      <SideMenu />
      <div className="mainColumn" css={mainColumnStyle}>
        <Header onClickRoomRate={() => setIsRoomRateModalOpen(true)} isRoomRateActive={isRoomRateModalOpen} />
        <div css={roomContainerStyle}>
          <div css={mainWrapperStyle}>
            <div
              id="roomsContainer"
              css={roomListContainerStyle}
              style={{ maxHeight: windows.height - HEADER_HEIGHT - 48, width: showAssignPanel ? 'calc(100% - 269px)' : '100%' }}
            >
              <LeftGuestRoom
                guestRooms={guestRooms}
                order={order}
                setOrder={v => {
                  fetchGuestRoomAssignStatus(v)
                  localStorage.setItem(roomOrderStorageKey, v)
                  setOrder(v)
                }}
                hiddenRoomNameList={hiddenRoomNameList}
                setHiddenRoomNameList={v => {
                  localStorage.setItem(roomNameCollapseStorageKey, JSON.stringify(v))
                  setHiddenRoomNameList(v)
                }}
                onClickNewReservation={() => setIsReservationCreateModalOpen(true)}
                bulkOperationMenu={<BulkOperationMenu hotelGuestRooms={hotelGuestRooms} onCompleted={reloadAfterBulkOperation} />}
              />
              <div css={dayListWrapperStyle}>
                <ul id="dayList" css={dayListSectionStyle}>
                  <DailyListHead today={today} dates={dates} />
                  <RemainingRoomTotalBody dates={dates} totalRemainingCountEachDate={totalRemainingCountEachDate}></RemainingRoomTotalBody>
                  <DailyListBody
                    guestRooms={guestRooms}
                    today={today}
                    dates={dates}
                    editGuestRooms={editGuestRooms}
                    roomAssignActiveId={roomAssignActiveId}
                    beginDate={beginDate}
                    endDate={endDate}
                    hiddenRoomNameList={hiddenRoomNameList}
                    siteControllerStock={siteControllerStock}
                    onClickStatusBar={onClickStatusBar}
                    clickToFreeDate={clickToFreeDate}
                    registerRoomRow={registerRoomRow}
                    registerRoomGroup={registerRoomGroup}
                  />
                </ul>
              </div>
            </div>
          </div>
          <RoomSummaryBar
            reservation={reservation}
            roomManagerTopSelectDate={roomManagerTopSelectDate}
            setRoomManagerTopSelectDate={setRoomManagerTopSelectDate}
            guestRooms={guestRooms}
            editGuestRooms={editGuestRooms}
            order={order}
            hiddenRoomNameList={hiddenRoomNameList}
            showAssignPanel={showAssignPanel}
            onClickUnattachedStatus={onClickStatusBar}
            assignPanelEditMode={assignPanelEditMode}
            setAutomaticAssignmentReservationsIds={setAutomaticAssignmentReservationsIds}
            assignPanel={() => (
              <AssignPanel
                reservation={reservation}
                reCreateAssignPanelDetail={reCreateAssignPanelDetail}
                reservationCheckinDate={reservationCheckinDate}
                setReservationCheckinDate={setReservationCheckinDate}
                reservationCheckoutDate={reservationCheckoutDate}
                setReservationCheckoutDate={setReservationCheckoutDate}
                editGuestRooms={editGuestRooms}
                setEditGuestRooms={setEditGuestRooms}
                setPreparationMemo={setPreparationMemo}
                preparationMemo={preparationMemo}
                guestRooms={guestRooms}
                setRoomDatesChangedGuestRooms={setRoomDatesChangedGuestRooms}
                setIsLoading={setIsLoading}
                setRoomAssignActiveId={setRoomAssignActiveId}
                hasTlTwoWayPlugin={hasTlTwoWayPlugin}
                hasRemoteLockPlugin={hasRemoteLockPlugin}
                setRoomManagerTopSelectDate={setRoomManagerTopSelectDate}
                setIsBar={setIsBar}
                preparation={preparation}
                setPreparation={setPreparation}
                changePreparation={changePreparation}
                assignPanelEditMode={assignPanelEditMode}
                setAssignPanelEditMode={setAssignPanelEditMode}
                fetchSelectableGuestRoom={fetchSelectableGuestRoom}
                fetchSelectableGuestRoomForPeriodChange={fetchSelectableGuestRoomForPeriodChange}
                initializeGuestRoomsStatuses={initializeGuestRoomsStatuses}
                deletedPreparationRooms={deletedPreparationRooms}
                setDeletedPreparationRooms={setDeletedPreparationRooms}
                clearRoomAssign={clearRoomAssign}
                order={order}
                fetchGuestRoomAssignStatus={fetchGuestRoomAssignStatus}
                hotelGuestRooms={hotelGuestRooms}
              />
            )}
          />
        </div>
        {automaticAssignmentReservationsIds?.length && (
          <div
            css={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 10,
            }}
          >
            <div css={{ backgroundColor: '#fff', padding: 32, borderRadius: 8 }}>
              <div css={{ fontWeight: 'bold', marginBottom: 32, fontSize: 18 }}>{t('Are you sure you want to assign?')}</div>
              <div css={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                <Button width={116} height={36} buttonType={3} onClick={() => setAutomaticAssignmentReservationsIds(undefined)}>
                  {t('Cancel')}
                </Button>
                <Button width={116} height={36} buttonType={2} onClick={makeAutomaticAssignment}>
                  {t('Assignment')}
                </Button>
              </div>
            </div>
          </div>
        )}
        {isReservationCreateModalOpen && (
          <ReservationCreateWithRoomModal
            setIsModalOpen={setIsReservationCreateModalOpen}
            onCreated={({ reservationId }) => {
              setReservation(undefined)
              history.replace({
                pathname: '/room-manager/daily',
                search: `?reservationId=${reservationId}`,
              })
            }}
          />
        )}
        {isRoomRateModalOpen && (
          <RoomRateDisplayModal initialDate={roomManagerTopSelectDate.format('YYYY-MM-DD')} onClose={() => setIsRoomRateModalOpen(false)} />
        )}
      </div>
      <LoadingFull isLoading={isLoading} />
    </div>
  )
}

const roomContainerStyle = css({
  //   display: 'flex',
  //   justifyContent: 'space-between',
  position: 'relative',
  height: 'calc(100vh - 78px)',
})

const mainWrapperStyle = css({
  //   width: '76%',
  //   width: 'calc(100% - 224px)',
  width: 'calc(100% - 230px)',
  padding: '24px 0 0 24px',
  height: '100%',
})

const roomListContainerStyle = css({
  boxShadow: '0px 0px 6px #0000001A',
  position: 'relative',
  borderRadius: '0 5px 5px 5px',
  width: '100%',
  display: 'flex',
  overflowX: 'scroll',
  height: '100%',
  backgroundColor: '#fff',
})

const dayListWrapperStyle = css({
  minWidth: '78%',
  display: 'flex',
})

const dayListSectionStyle = css({
  height: 'max-content',
})
